<template>
  <content-popup
    ref="popup"
    class="image-annotation-config-popup"
    :visible="visible"
    :reference-element="referenceElement"
    :show-close-button="true"
    :show-cancel-button="false"
    :show-save-button="false"
    :auto-scroll="false"
    placement="bottom-end"
    :offset="popupOffset"
    size="small"
    @on-close="handleClose"
  >
    <template #content>
      <div v-if="showColorPicker" class="overlay-head">
        <!-- Color selection -->
        <div class="picker-wrapper">
          <div class="label">{{ t("tinymce_plugin_drawing_modal_colour_label") }}</div>
          <div class="color-picker">
            <span
              v-for="color in colors"
              :key="color.value"
              :class="{ selected: isSelectedColor(color.value) }"
              :style="{ backgroundColor: color.value }"
              class="color-option"
              @click="handleColorSelect(color.value)"
            />
          </div>
        </div>

        <!-- Thickness selection -->
        <div v-if="props.selectedTool === 'draw'" class="picker-wrapper">
          <div class="label">{{ t("tinymce_plugin_drawing_modal_thickness_label") }}</div>
          <div class="thickness-picker">
            <div
              v-for="size in thicknessSizes"
              :key="size"
              :class="{ selected: selectedThickness === size }"
              class="thickness-option"
              @click="handleThicknessSelect(size)"
            >
              <div class="thickness-preview">
                <div
                  class="thickness-line"
                  :style="{
                    height: `${Math.min(size * 0.667, 40)}px`,
                    backgroundColor: '#0B233F',
                  }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="props.selectedTool === 'stamp'" class="picker-wrapper">
        <div class="label">{{ t("image_annotation_toolbar_tooltip_stamp") }}</div>
        <div class="stamp-picker">
          <div
            v-for="(stamp, index) in stamps"
            :key="index"
            class="button-wrapper"
            @click="handleStampSelected(stamp)"
          >
            <span class="annotation-tool button" :class="generateToolBarClass(stamp)" />
          </div>
        </div>
      </div>
    </template>
  </content-popup>
</template>

<script setup lang="ts">
import { ColorItem } from "@plugins/drawing/types.ts";
import { CANVAS_COLORS, PEN_HIGHLIGHTER_SIZE } from "@plugins/drawing/drawing.config";
import { ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import { useAnnotation } from "@modules/annotation/composables/annotation.ts";

import {
  ANNOTATION_TYPE,
  IMAGE_ANNOTATION_POPUP_OFFSET,
} from "@modules/annotation/service/annotation.constant";

interface Props {
  visible: boolean;
  referenceElement: HTMLElement | null;
  selectedTool: ImageAnnotationTool | null;
  selectedColor: string | null;
  selectedThickness: number | null;
  selectedStamp: string | null;
}

type ImageAnnotationTool = "draw" | "stamp" | "text";

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  onColorChange: [color: string];
  onThicknessChange: [thickness: number];
  onStampSelected: [stampShape: string];
}>();

const { t } = useI18n();
const { getToolbarConfig } = useAnnotation();

const colors = ref<ColorItem[]>(CANVAS_COLORS);
const thicknessSizes = ref<number[]>(PEN_HIGHLIGHTER_SIZE);

// Anchor the popup to the bottom-right corner of the canvas, inset by IMAGE_ANNOTATION_POPUP_OFFSET.
// Placement is "bottom-end", so shift left by the inset and up by (popup height + inset).
const popupOffset = ({ popper }: { popper: { height: number } }): [number, number] => [
  -IMAGE_ANNOTATION_POPUP_OFFSET,
  -(popper.height + IMAGE_ANNOTATION_POPUP_OFFSET),
];

const stamps = computed(() => {
  return getToolbarConfig(ANNOTATION_TYPE.IMAGE_ANNOTATION) || [];
});

const showColorPicker = computed(
  () => props.selectedTool === "draw" || props.selectedTool === "text",
);

const isSelectedColor = (color: string) => {
  return props.selectedColor === color;
};

const handleColorSelect = (color: string) => {
  emit("onColorChange", color);
};

const handleThicknessSelect = (thickness: number) => {
  emit("onThicknessChange", thickness);
};

const handleStampSelected = (stamp: { color: string; shape: string }) => {
  emit("onStampSelected", stamp.shape);
};

const generateToolBarClass = (stamp: { color: string; shape: string }) => {
  return `${stamp.color} ${stamp.shape}`;
};

const handleClose = () => {
  emit("close");
};
</script>
<style lang="scss">
@use "@styles/themes/sls-theme/tokens/typo" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;

.image-annotation-config-popup {
  .content-popup.is-visible {
    .wrapper {
      max-width: 210px;
      margin: 0;
    }
  }

  .content-popup-header {
    position: absolute;
    right: 0;
    top: 0;
    padding: 0;

    .bx--link.close {
      transform: scale(0.75);
    }
  }

  .picker-wrapper {
    .label {
      @include carbon--type-style("productive-heading-01");
      margin-bottom: $spacing-03;
    }

    &:not(:first-of-type) {
      margin-top: $spacing-05;
    }
  }

  .content-popup-content {
    padding: $spacing-03;
  }

  .thickness-picker {
    display: flex;
    gap: $spacing-03;
    margin-top: $spacing-05;
    align-items: center;
  }

  .thickness-preview {
    width: inherit;
  }

  .thickness-option {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 2rem;
    width: 2rem;
    padding: $spacing-03;
    background-color: $ui-02;
    border-radius: 8px;
    cursor: pointer;
    &.selected {
      outline: 2px solid $interactive-01;
    }
  }

  .thickness-line {
    width: 22px;
    border-radius: 20px;
  }

  .button-wrapper {
    display: flex;
    padding: $spacing-03;
    border-radius: 5px;
    cursor: pointer;

    &:hover {
      background-color: $hover-ui;
    }
  }

  .stamp-picker {
    display: flex;

    .annotation-tool {
      width: 2rem;
      height: 2rem;
      background-repeat: no-repeat;

      &:not(:first-of-type) {
        margin-left: $spacing-06;
      }

      &.highlight-1 {
        background: url("/src/assets/images/icons/misc/32px/AnnotateTick32.svg");
      }
      &.highlight-2 {
        background: url("/src/assets/images/icons/misc/32px/AnnotateCross32.svg");
      }
      &.highlight-3 {
        background: url("/src/assets/images/icons/misc/32px/AnnotateCircle32.svg");
      }
      &.assess-1 {
        background: url("/src/assets/images/icons/misc/32px/AssessmentTick32.svg");
      }
      &.assess-2 {
        background: url("/src/assets/images/icons/misc/32px/AssessmentCross32.svg");
      }
      &.assess-3 {
        background: url("/src/assets/images/icons/misc/32px/AssessmentCircle32.svg");
      }
    }
  }

  .color-picker {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: $spacing-03;

    span {
      display: inline-block;
      width: 32px;
      height: 32px;
      padding: $spacing-01;
      position: relative;
      outline: 1px solid $toolbar-background-01;
      border-radius: 50%;
      background-clip: content-box;
      cursor: pointer;

      &.White {
        box-shadow: inset 0 0 0 1px $ui-06;

        &.selected {
          box-shadow: none;
        }
      }

      &.selected {
        outline: 2px solid $interactive-01;
        border-color: $interactive-04;
      }
    }
  }
}
</style>
